'use client';
import {
  FontWeight,
  Select,
  type SelectOption,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/compat/router';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  createContext,
  type FC,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { convertUrlSearchParamsToObject } from '../../utils/url';

import { type BootcampCohortSelectorBootcampCohortFragment } from './__generated-gql-types__/BootcampCohortSelector.generated';
import { formatBootcampCohortDate } from './utils';

interface BootcampCohortSelectorState {
  cohorts: BootcampCohortSelectorBootcampCohortFragment[];
}

const BootcampCohortSelectorContext =
  createContext<BootcampCohortSelectorState>({
    get cohorts(): never {
      throw new Error(
        'Please wrap your app in <BootcampCohortSelectorProvider>',
      );
    },
  });

export const BootcampCohortSelector: FC = () => {
  const { cohorts } = useContext(BootcampCohortSelectorContext);
  const [selectedCohort, setSelectedCohort] = useBootcampCohortSelector();

  const cohortOptions: Array<
    SelectOption<Scalars['BootcampCohortUrn']['output']>
  > = cohorts.map((cohort) => ({
    label: formatBootcampCohortDate(cohort),
    value: cohort.id,
  }));

  if (cohorts.length === 0) {
    return null;
  }

  return (
    <Select
      options={cohortOptions}
      value={selectedCohort?.id}
      onValueChange={setSelectedCohort}
      fontWeight={FontWeight.MEDIUM}
    />
  );
};

interface BootcampCohortSelectorProviderProps
  extends BootcampCohortSelectorState {
  children: React.ReactNode;
}

export const BootcampCohortSelectorProvider: FC<
  BootcampCohortSelectorProviderProps
> = ({ cohorts, children }) => {
  return (
    <BootcampCohortSelectorContext.Provider value={{ cohorts }}>
      {children}
    </BootcampCohortSelectorContext.Provider>
  );
};

export const useBootcampCohortSelector = (): [
  BootcampCohortSelectorBootcampCohortFragment | null,
  (cohortId: Scalars['BootcampCohortUrn']['output']) => void,
] => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { cohorts } = useContext(BootcampCohortSelectorContext);
  const selectedCohort: BootcampCohortSelectorBootcampCohortFragment | null =
    useMemo(() => {
      const cohortId = searchParams.get('cohortId');
      if (typeof cohortId === 'string') {
        return cohorts.find((cohort) => cohort.id === cohortId) ?? null;
      }
      return cohorts[0] ?? null;
    }, [searchParams, cohorts]);

  const setSelectedCohort = useCallback(
    (cohortId: Scalars['BootcampCohortUrn']['output']) => {
      void router?.replace(
        {
          pathname,
          query: { ...convertUrlSearchParamsToObject(searchParams), cohortId },
        },
        undefined,
        {
          shallow: true,
        },
      );
    },
    [router, searchParams, pathname],
  );

  return [selectedCohort, setSelectedCohort];
};
